import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import DeployApi from './DeployApi';

// 배포 화면.
//
// 처음엔 "환경 고르고 · 무엇 고르고 · 브랜치 적고 · 버튼" 이었다. 그러려면
// 무엇을 배포해야 하는지를 사람이 이미 알고 있어야 한다. 그게 어렵다.
//
// 그래서 서비스 목록 자체를 화면으로 삼았다. 줄마다 지금 올라가 있는 것과
// 올릴 새 것이 나란히 보이고, 올릴 게 있으면 그 줄만 눈에 띈다. 누르면 그
// 자리에서 펼쳐져 단계와 로그가 흐른다. 다른 데로 가지 않는다.
//
// 브랜치 입력칸은 없앴다. 어느 브랜치를 쓸지는 그 서버의 배포 정의가
// 정한다 — 개발은 develop, 운영은 main. 화면에서 고르게 두면 개발 서버에
// main 을 올리는 일이 생긴다. 대신 어느 브랜치인지는 두 곳에 보여 준다.
//
// 이 화면은 자기 환경만 다룬다. IWINV 관리자는 운영만, 헤츠너 관리자는
// 개발만 — 한 화면에서 둘 다 되게 두면 운영에 잘못 누를 여지가 남는다.

// 관리자 화면은 어두운 테마다. 밝은 색을 쓰면 글자가 바탕에 묻혀
// 제목조차 안 읽힌다 — 실제로 그렇게 만들어 두었다가 브라우저로 띄워 보고
// 알았다. 서버 모니터링 화면과 같은 팔레트를 쓴다.
const C = {
  text: '#ffffff',
  muted: 'rgba(255,255,255,0.65)',
  dim: 'rgba(255,255,255,0.45)',
  card: 'linear-gradient(180deg, rgba(15,38,59,0.65) 0%, rgba(10,25,41,0.78) 100%)',
  border: 'rgba(139,210,244,0.12)',
  soft: 'rgba(255,255,255,0.04)',
  line: 'rgba(255,255,255,0.10)',
  accent: '#01BCD6',
  ok: '#86efac',
  okBg: 'rgba(34,197,94,0.14)',
  okLine: 'rgba(134,239,172,0.35)',
  info: '#93c5fd',
  infoBg: 'rgba(59,130,246,0.16)',
  infoLine: 'rgba(147,197,253,0.35)',
  warn: '#fbbf24',
  warnBg: 'rgba(251,191,36,0.14)',
  warnLine: 'rgba(251,191,36,0.35)',
  bad: '#fca5a5',
  badBg: 'rgba(248,113,113,0.14)',
  badLine: 'rgba(248,113,113,0.38)',
  ink: '#0b1626',
};

const Wrap = styled.div`padding: 22px 26px 60px; max-width: 1080px;`;

const Head = styled.div`
  margin-bottom: 16px;
  h1 { margin: 0 0 6px; font-size: 21px; color: ${C.text}; }
  p { margin: 0; font-size: 13px; color: ${C.muted}; line-height: 1.6; }`;

// ── 환경 ─────────────────────────────────────────────────────────
// 운영에 잘못 누르는 일이 가장 무섭다. 탭을 크게 하고 색을 갈라 둔다.
const Tabs = styled.div`display: flex; gap: 0; margin-bottom: 18px; border-bottom: 2px solid ${C.line};`;

const Tab = styled.button`
  padding: 12px 22px; border: none; background: none; cursor: pointer;
  font-family: inherit; font-size: 14px; font-weight: 700;
  color: ${p => (p.$on ? (p.$prod ? '#fca5a5' : '#fbbf24') : 'rgba(255,255,255,0.45)')};
  border-bottom: 3px solid ${p => (p.$on ? (p.$prod ? '#f87171' : '#fbbf24') : 'transparent')};
  margin-bottom: -2px;
  small { display: block; font-weight: 500; font-size: 11px; margin-top: 3px; opacity: 0.85; }`;

const Banner = styled.div`
  padding: 11px 14px; border-radius: 9px; margin-bottom: 16px;
  font-size: 12.5px; line-height: 1.6;
  background: ${p => (p.$prod ? C.badBg : C.warnBg)};
  border: 1px solid ${p => (p.$prod ? C.badLine : C.warnLine)};
  color: ${p => (p.$prod ? C.bad : C.warn)};`;

// ── 서비스 줄 ────────────────────────────────────────────────────
const List = styled.div`display: flex; flex-direction: column; gap: 10px;`;

const Card = styled.section`
  border: 1px solid ${p => (p.$failed ? C.badLine : p.$behind ? C.infoLine : C.border)};
  border-radius: 11px; background: ${C.card}; overflow: hidden;
  box-shadow: ${p => (p.$open ? '0 4px 18px rgba(0,0,0,0.28)' : 'none')};`;

const Row = styled.div`
  display: flex; align-items: center; gap: 14px; padding: 14px 16px; flex-wrap: wrap;`;

const Who = styled.div`
  flex: 1; min-width: 210px;
  strong { display: block; font-size: 14.5px; color: ${C.text}; }
  small { display: block; margin-top: 3px; font-size: 11.5px; color: ${C.dim};
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 460px; }`;

// 이 줄이 눌러야 할 줄인지 한눈에 보이게 한다.
const State = styled.span`
  font-size: 12px; font-weight: 700; padding: 5px 11px; border-radius: 999px;
  white-space: nowrap;
  background: ${p => ({ new: C.infoBg, unknown: C.soft, fail: C.badBg }[p.$tone] || C.okBg)};
  color: ${p => ({ new: C.info, unknown: C.dim, fail: C.bad }[p.$tone] || C.ok)};
  border: 1px solid ${p => ({ new: C.infoLine, unknown: C.line, fail: C.badLine }[p.$tone] || C.okLine)};`;

// 같은 환경에 서버가 여러 대다. 어디 것인지 작게 붙여 둔다.
const Where = styled.span`
  margin-left: 8px; font-size: 10.5px; font-weight: 600; color: ${C.dim};
  background: ${C.soft}; border: 1px solid ${C.line}; border-radius: 5px; padding: 1px 6px;
  vertical-align: middle;`;

const Go = styled.button`
  padding: 9px 18px; border-radius: 8px; font-family: inherit;
  font-size: 13px; font-weight: 700; cursor: pointer; white-space: nowrap;
  border: 1px solid ${p => (p.$strong ? 'transparent' : C.line)};
  background: ${p => (p.$strong ? (p.$prod ? '#dc2626' : C.accent) : 'transparent')};
  color: ${p => (p.$strong ? (p.$prod ? '#fff' : C.ink) : C.muted)};
  &:disabled { background: ${C.soft}; color: ${C.dim}; border-color: ${C.line}; cursor: not-allowed; }`;

const More = styled.button`
  border: none; background: none; cursor: pointer; font-family: inherit;
  font-size: 12px; color: ${C.dim}; padding: 4px 6px;`;

// ── 펼친 속 ──────────────────────────────────────────────────────
const Body = styled.div`border-top: 1px solid ${C.line}; padding: 14px 16px; background: rgba(0,0,0,0.18);`;

const Pipeline = styled.div`display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 12px;`;

const Stage = styled.div`
  flex: 1 1 0; min-width: 86px; padding: 8px 6px; text-align: center; border-radius: 8px;
  border: 1px solid ${p => p.$border}; background: ${p => p.$bg}; color: ${p => p.$fg};
  strong { display: block; font-size: 11.5px; font-weight: 700; }
  small { display: block; margin-top: 2px; font-size: 10.5px; opacity: 0.85; }`;

const COLORS = {
  idle: { bg: C.soft, border: C.line, fg: C.dim, mark: '·', word: '대기' },
  start: { bg: C.infoBg, border: C.infoLine, fg: C.info, mark: '◐', word: '진행 중' },
  ok: { bg: C.okBg, border: C.okLine, fg: C.ok, mark: '✓', word: '완료' },
  fail: { bg: C.badBg, border: C.badLine, fg: C.bad, mark: '✕', word: '실패' },
};

const LogHead = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  margin-bottom: 7px; font-size: 12px; font-weight: 700; color: ${C.muted};
  button { border: 1px solid ${C.line}; background: transparent; color: ${C.muted}; border-radius: 6px;
    padding: 3px 9px; font-size: 11px; cursor: pointer; font-family: inherit; font-weight: 600; }`;

const LogBox = styled.pre`
  margin: 0; padding: 12px 14px; border-radius: 9px; background: #0f172a; color: #e2e8f0;
  font-size: 12px; line-height: 1.6; max-height: 320px; overflow: auto;
  white-space: pre-wrap; word-break: break-word; font-family: 'D2Coding', Consolas, monospace;`;

const Line = styled.div`
  color: ${p => (p.$fail ? '#fca5a5' : p.$ok ? '#86efac' : p.$step ? '#93c5fd' : '#e2e8f0')};
  font-weight: ${p => (p.$ok || p.$fail ? 700 : 400)};`;

const Done = styled.div`
  margin-bottom: 10px; padding: 9px 12px; border-radius: 8px; font-size: 12.5px; font-weight: 700;
  background: ${p => (p.$ok ? C.okBg : C.badBg)};
  border: 1px solid ${p => (p.$ok ? C.okLine : C.badLine)};
  color: ${p => (p.$ok ? C.ok : C.bad)};
  span { font-weight: 400; color: ${C.muted}; margin-left: 8px; }`;

// 실패한 까닭. 로그를 끝까지 읽지 않아도 보이게 맨 위에 둔다.
const Why = styled.pre`
  margin: 0 0 10px; padding: 10px 12px; border-radius: 8px;
  background: rgba(248,113,113,0.10); border: 1px solid ${C.badLine}; color: ${C.bad};
  font-size: 11.5px; line-height: 1.6; white-space: pre-wrap; word-break: break-word;
  max-height: 160px; overflow: auto; font-family: 'D2Coding', Consolas, monospace;`;

// ── 되돌리기 ─────────────────────────────────────────────────────
const Past = styled.div`margin-top: 14px; border-top: 1px dashed ${C.line}; padding-top: 10px;`;

const PastHead = styled.div`
  font-size: 12px; font-weight: 700; color: ${C.muted}; margin-bottom: 6px;
  small { font-weight: 400; color: ${C.dim}; margin-left: 8px; }`;

const PastRow = styled.div`
  display: flex; align-items: center; gap: 9px; padding: 6px 0; font-size: 12px; color: ${C.muted};
  border-bottom: 1px solid ${C.line};
  &:last-child { border-bottom: none; }
  code { font-size: 11px; color: ${C.dim}; font-family: Consolas, monospace; }
  span.s { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  em { font-style: normal; font-size: 10.5px; font-weight: 700; color: ${C.ok};
       background: ${C.okBg}; border: 1px solid ${C.okLine}; border-radius: 5px; padding: 2px 6px; }
  button { border: 1px solid ${C.line}; background: transparent; color: ${C.muted}; border-radius: 6px;
    padding: 4px 9px; font-size: 11px; font-weight: 700; cursor: pointer; font-family: inherit;
    white-space: nowrap; }
  button:disabled { color: ${C.dim}; cursor: not-allowed; }`;

const Empty = styled.p`margin: 0; padding: 26px 0; text-align: center; font-size: 13px; color: ${C.dim};`;

// 사람이 눌러 배포하는 길과, 프로그램이 부르는 길. 둘을 한 화면에 둔다.
const TopTabs = styled.div`display: flex; gap: 6px; margin-bottom: 18px;`;

const TopTab = styled.button`
  padding: 8px 16px; border-radius: 999px; cursor: pointer; font-family: inherit;
  font-size: 13px; font-weight: 700;
  border: 1px solid ${p => (p.$on ? C.accent : C.line)};
  background: ${p => (p.$on ? C.accent : 'transparent')};
  color: ${p => (p.$on ? C.ink : C.muted)};`;

// 서버마다 같은 이름의 프로젝트가 있을 수 있다. 줄을 가릴 때는 서버까지 본다.
const rowKey = (svc) => `${svc.hostKey}:${svc.key}`;

// ─────────────────────────────────────────────────────────────────
export default function Deploy() {
  const [targets, setTargets] = useState([]);
  const [view, setView] = useState('deploy');          // deploy | api
  const [target, setTarget] = useState('development'); // 개발이 기본
  const [services, setServices] = useState([]);
  const [unreachable, setUnreachable] = useState([]);
  const [pipelines, setPipelines] = useState({});   // key → [{key,label}]
  const [points, setPoints] = useState({});         // key → 되돌릴 지점
  const [loading, setLoading] = useState(true);

  const [openKey, setOpenKey] = useState(null);     // 펼친 서비스
  const [running, setRunning] = useState(null);     // 도는 중인 서비스
  const [stages, setStages] = useState({});
  const [lines, setLines] = useState([]);
  const [result, setResult] = useState(null);
  // 서비스별 지난 결과. 접어 두어도 "실패했다" 가 줄에 남아야 한다.
  const [lastResult, setLastResult] = useState({});
  const [follow, setFollow] = useState(true);
  const logRef = useRef(null);
  const esRef = useRef(null);

  const isProd = target === 'production';
  // 로컬 → develop(개발 서버) → main(운영 서버). 어느 브랜치를 보는지
  // 안 보이면 '무엇이 올라가는지' 를 반만 아는 셈이다.
  const branches = [...new Set(services.map(s => s.branch).filter(Boolean))];

  const load = useCallback(() => {
    const q = encodeURIComponent(target);
    return Promise.all([
      fetch(`/api/admin/deploy/targets?target=${q}`, { credentials: 'include' }).then(r => r.json()),
      fetch(`/api/admin/deploy/status?target=${q}`, { credentials: 'include' }).then(r => r.json()),
      fetch(`/api/admin/deploy/rollback-points?target=${q}`, { credentials: 'include' }).then(r => r.json()),
    ]).then(([t, s, p]) => {
      if (t?.success) {
        const list = t.targets || [];
        setTargets(list);
        // 이 서버가 다루는 환경이 하나면 그것으로 맞춘다. 개발로 박아 두면
        // 운영 서버에서 열었을 때 빈 화면이 된다.
        if (list.length === 1 && list[0].key !== target) setTarget(list[0].key);
        // 서버마다 같은 이름의 프로젝트가 있을 수 있다. 서버까지 묶어 구분한다.
        setPipelines(Object.fromEntries(
          (t.projects || []).map(x => [`${x.hostKey}:${x.key}`, x.stages])));
      }
      if (s?.success) {
        setServices(s.services || []);
        setUnreachable(s.unreachable || []);
      }
      if (p?.success) setPoints(p.points || {});
    }).catch(() => {});
  }, [target]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setOpenKey(null);
    load().finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [load]);

  useEffect(() => () => { try { esRef.current?.close(); } catch (_) {} }, []);

  useEffect(() => {
    if (follow && logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [lines, follow]);

  // 배포와 되돌리기가 같은 길을 탄다 — 서버가 같은 표식을 찍고 화면도
  // 같은 그림을 쓴다. 다른 것은 어느 주소를 여느냐뿐이다.
  const start = useCallback((key, url, kind) => {
    setOpenKey(key); setRunning(key);
    setStages({}); setLines([]); setResult(null); setFollow(true);
    // 지난 결과는 지운다. 도는 동안 옛 실패가 남아 있으면 헷갈린다.
    setLastResult(prev => ({ ...prev, [key]: null }));
    const es = new EventSource(url, { withCredentials: true });
    esRef.current = es;

    es.onmessage = (ev) => {
      let m; try { m = JSON.parse(ev.data); } catch (_) { return; }
      if (m.type === 'stage') setStages(s => ({ ...s, [m.key]: m.status }));
      else if (m.type === 'log') setLines(l => [...l, m.line]);
      else if (m.type === 'end') {
        const r = { ok: m.ok, seconds: m.seconds, kind,
                    failedStage: m.failedStage || null, reason: m.reason || [] };
        setResult(r);
        // 접어 두어도 "실패했다" 가 줄에 남아야 한다.
        setLastResult(prev => ({ ...prev, [key]: r }));
        setRunning(null);
        try { es.close(); } catch (_) {}
        load(); // 지금 올라간 것이 바뀌었다
      }
    };
    es.onerror = () => {
      setLines(l => [...l, 'FAIL 연결이 끊겼습니다. 일은 서버에서 계속 돌 수 있습니다.']);
      const r = { ok: false, kind, failedStage: null,
                  reason: ['연결이 끊겼습니다. 일은 서버에서 계속 돌 수 있습니다.'] };
      setResult(r);
      setLastResult(prev => ({ ...prev, [key]: r }));
      setRunning(null);
      try { es.close(); } catch (_) {}
    };
  }, [load]);

  const deploy = useCallback((svc) => {
    // 운영은 한 번 더 묻는다. 개발은 자주 누르는 곳이라 묻지 않는다.
    if (isProd && !window.confirm(
      `운영에 ${svc.label} 을(를) 배포합니다.  (${svc.branch || 'main'} 브랜치)\n\n` +
      `지금: ${svc.current} ${svc.currentSubject}\n` +
      `올라갈 것: ${svc.next || '(최신)'} ${svc.nextSubject}\n\n진행할까요?`)) return;
    const qs = new URLSearchParams({
      // 브랜치를 박지 않는다. 그 서버의 배포 정의가 정한다(개발 develop,
      // 운영 main). 'main' 으로 박으면 개발에 운영 코드가 올라간다.
      project: svc.key, target, host: svc.hostKey,
      ...(svc.branch ? { branch: svc.branch } : {}),
    }).toString();
    start(rowKey(svc), `/api/admin/deploy/stream?${qs}`, 'deploy');
  }, [isProd, target, start]);

  const rollback = useCallback((svc, point) => {
    if (!window.confirm(
      `${svc.label} 을(를) 이 자리로 되돌립니다.\n\n${point.commit} ${point.subject || ''}\n\n` +
      `${point.hasBuild ? '' : '이 지점에는 그때의 build 가 없어 화면은 지금 것이 남습니다.\n\n'}진행할까요?`)) return;
    const qs = new URLSearchParams({
      project: svc.key, target, host: svc.hostKey, stamp: point.stamp,
    }).toString();
    start(rowKey(svc), `/api/admin/deploy/rollback-stream?${qs}`, 'rollback');
  }, [target, start]);

  const busy = running !== null;

  return (
    <Wrap>
      <Head>
        <h1>배포</h1>
        <p>{view === 'deploy'
          ? '올릴 새 것이 있는 서비스가 위로 옵니다. 배포하기를 누르면 받기 → 빌드 → 재시작까지 한 번에 하고, 도중에 실패하면 직전 상태로 되돌리므로 서비스는 멈추지 않습니다.'
          : 'CI 나 스크립트가 부르는 길입니다. 토큰을 만들고, 어떻게 부르는지 예시를 그대로 복사해 쓰면 됩니다.'}</p>
      </Head>

      <TopTabs>
        <TopTab type="button" $on={view === 'deploy'} onClick={() => setView('deploy')}>
          눌러서 배포
        </TopTab>
        <TopTab type="button" $on={view === 'api'} onClick={() => setView('api')}>
          API 로 배포
        </TopTab>
      </TopTabs>

      {view === 'api' && <DeployApi />}
      {view === 'deploy' && (<>

      {/* 이 서버가 다루는 환경만 온다. 하나뿐이면 고를 것이 없으니
          탭 대신 어느 환경인지만 알려 준다 — 누를 게 없는 탭은 군더더기다. */}
      <Tabs>
        {targets.map(t => (
          <Tab key={t.key} type="button" $on={target === t.key} $prod={t.key === 'production'}
            onClick={() => !busy && setTarget(t.key)} disabled={busy}>
            {t.key === 'production' ? '운영' : '개발'}
            <small>{t.label.replace(/^(운영|개발)\s*·\s*/, '')}</small>
          </Tab>
        ))}
      </Tabs>

      <Banner $prod={isProd}>
        {isProd
          ? '운영은 실제 사용자가 보는 곳입니다. 개발에서 먼저 확인한 뒤 올리세요.'
          : '개발은 헤츠너(.kr)입니다. 여기서 만진 것은 운영 화면에 닿지 않습니다.'}
        {branches.length > 0 && <> 브랜치는 <b>{branches.join(' · ')}</b> 를 봅니다.</>}
      </Banner>

      {unreachable.length > 0 && (
        <Banner $prod={false} style={{ background: C.badBg, borderColor: C.badLine, color: C.bad }}>
          닿지 못한 서버가 있습니다 — {unreachable.map(u => u.name).join(', ')}.
          그 서버의 서비스는 목록에 없습니다.
        </Banner>
      )}

      {loading && <Empty>불러오는 중…</Empty>}
      {!loading && services.length === 0 && <Empty>이 환경에 배포할 수 있는 것이 없습니다.</Empty>}

      <List>
        {services.map((svc) => {
          const id = rowKey(svc);
          const open = openKey === id;
          const isRunning = running === id;
          const stageList = pipelines[id] || [];
          const past = points[id] || [];
          const hasNew = svc.behind > 0;
          const tone = svc.behind === null ? 'unknown' : hasNew ? 'new' : 'ok';
          const last = lastResult[id];
          const failed = last && !last.ok;
          const failedLabel = failed && last.failedStage
            ? (stageList.find(x => x.key === last.failedStage)?.label || last.failedStage)
            : null;

          return (
            <Card key={id} $open={open} $behind={hasNew} $failed={failed}>
              <Row>
                <Who>
                  <strong>
                    {svc.label}
                    {/* 같은 환경에 서버가 여러 대다. 어디 것인지 보여야 한다. */}
                    <Where>{svc.hostName}</Where>
                    {svc.branch && <Where>{svc.branch}</Where>}
                  </strong>
                  {/* 지금 무엇이 올라가 있는지. 이게 없으면 눌러야 할지 알 수 없다. */}
                  <small title={svc.currentSubject}>
                    지금 {svc.current} · {svc.currentSubject || '(설명 없음)'}
                  </small>
                  {hasNew && (
                    <small style={{ color: C.info }} title={svc.nextSubject}>
                      올라갈 것 {svc.next} · {svc.nextSubject || '(설명 없음)'}
                    </small>
                  )}
                </Who>

                {/* 실패는 접어 두어도 보여야 한다. 어느 단계에서 깨졌는지까지. */}
                {failed && !isRunning
                  ? <State $tone="fail">
                      {failedLabel ? `${failedLabel}에서 실패` : '실패'}
                    </State>
                  : <State $tone={tone}>
                      {svc.behind === null ? '확인 못 함' : hasNew ? `새 버전 ${svc.behind}개` : '최신'}
                    </State>}

                <Go type="button" $strong={hasNew || failed} $prod={isProd}
                  disabled={busy} onClick={() => deploy(svc)}>
                  {isRunning ? '배포 중…' : failed ? '다시 시도' : hasNew ? '배포하기' : '다시 배포'}
                </Go>

                <More type="button" onClick={() => setOpenKey(open ? null : id)}>
                  {open ? '접기' : `자세히 (${stageList.length}단계)`}
                </More>
              </Row>

              {open && (
                <Body>
                  <Pipeline>
                    {stageList.map(({ key, label }) => {
                      // 도는 중이 아니면 지난 실패 자리를 그대로 남겨 둔다.
                      const st = (openKey === id && (isRunning || result))
                        ? (stages[key] || 'idle')
                        : (last && last.failedStage === key ? 'fail' : 'idle');
                      const c = COLORS[st];
                      return (
                        <Stage key={key} $bg={c.bg} $border={c.border} $fg={c.fg}
                          title={`${label} — ${c.word}`}>
                          <strong>{c.mark} {label}</strong>
                          <small>{c.word}</small>
                        </Stage>
                      );
                    })}
                  </Pipeline>

                  {last && openKey === id && (
                    <Done $ok={last.ok}>
                      {last.kind === 'rollback'
                        ? (last.ok ? '되돌렸습니다.' : '되돌리지 못해 원래대로 두었습니다.')
                        : last.ok
                          ? '배포했습니다.'
                          : failedLabel
                            ? `${failedLabel} 단계에서 실패했습니다. 직전 상태로 되돌렸습니다.`
                            : '실패해서 직전 상태로 되돌렸습니다.'}
                      {last.seconds != null && <span>{last.seconds}초</span>}
                    </Done>
                  )}

                  {/* 왜 실패했는지. 로그를 끝까지 읽지 않아도 보이게 위에 둔다. */}
                  {last && !last.ok && last.reason?.length > 0 && openKey === id && (
                    <Why>{last.reason.join('\n')}</Why>
                  )}

                  {(isRunning || lines.length > 0) && (
                    <>
                      <LogHead>
                        <span>로그 {lines.length ? `(${lines.length}줄)` : ''}</span>
                        <button type="button" onClick={() => setFollow(f => !f)}>
                          {follow ? '따라가기 끄기' : '맨 아래로'}
                        </button>
                      </LogHead>
                      <LogBox ref={logRef}>
                        {isRunning && lines.length === 0 && <Line $step>시작하는 중…</Line>}
                        {lines.map((line, i) => (
                          <Line key={i}
                            $ok={line.startsWith('OK ')}
                            $fail={line.startsWith('FAIL')}
                            $step={line.startsWith('--') || line.startsWith('==')}>
                            {line || ' '}
                          </Line>
                        ))}
                      </LogBox>
                    </>
                  )}

                  <Past>
                    <PastHead>
                      되돌리기
                      <small>배포한 뒤에 문제를 발견했을 때. 도중 실패는 알아서 되돌아갑니다.</small>
                    </PastHead>
                    {past.length === 0 ? (
                      <p style={{ margin: 0, fontSize: 12, color: C.dim }}>
                        아직 없습니다. 한 번 배포하면 그 직전 자리가 여기 남습니다.
                      </p>
                    ) : past.map(p => (
                      <PastRow key={p.stamp}>
                        <code>{p.commit}</code>
                        <span className="s">{p.subject || '(설명 없음)'}</span>
                        {!p.hasBuild && <code title="그때의 build 가 없습니다">build 없음</code>}
                        {p.current
                          ? <em>지금 이 자리</em>
                          : <button type="button" disabled={busy} onClick={() => rollback(svc, p)}>
                              이 자리로
                            </button>}
                      </PastRow>
                    ))}
                  </Past>
                </Body>
              )}
            </Card>
          );
        })}
      </List>
      </>)}
    </Wrap>
  );
}
